import React, { useEffect, useMemo, useState } from "react";
import "../ln.scss";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import url from "../../../url.json";
import headers from "../../../config/headers.js";
import { sanitizeHtml } from "../../../config/richText";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

const formatDate = (value) => {
  if (!value) return "";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : value;
};

// CKEditor matnidagi qo'lda qo'yilgan bo'sh joylar (&nbsp;) dizaynni buzmasin
const cleanHtml = (html) =>
  (html || "").replace(/(<p>)(\s|&nbsp;)+/g, "$1").replace(/<p>(\s|&nbsp;)*<\/p>/g, "");

const plainText = (html) => String(html || "").replace(/<[^>]*>|&nbsp;/g, " ").replace(/\s+/g, " ").trim();

function BlogOpen({ t }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get("id");
  const [article, setArticle] = useState();
  const [others, setOthers] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound
  const [copied, setCopied] = useState(false);
  const lan = useSelector((state) => state.langReducer);

  useEffect(() => {
    setStatus("loading");
    window.scrollTo(0, 0);
    axios
      .get(url.url + `/article/${id}`, { headers: headers() })
      .then((res) => {
        setArticle(res.data.data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [id, lan.value]);

  useEffect(() => {
    axios
      .get(url.url + "/article", { headers: headers(), params: { per_page: 5 } })
      .then((res) => setOthers(res.data.data || []))
      .catch(() => setOthers([]));
  }, [lan.value]);

  const html = useMemo(() => sanitizeHtml(cleanHtml(article?.text)), [article]);
  const readMinutes = useMemo(() => Math.max(1, Math.round(plainText(article?.text).split(" ").length / 180)), [article]);
  const more = others.filter((a) => String(a.id) !== String(id)).slice(0, 4);

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="ln ln-doc ln-post">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Vis Nefrit | {article?.title || t("News")}</title>
        {article ? <meta name="description" content={plainText(article.text).slice(0, 160)} /> : null}
      </Helmet>

      <div className="ln-doc__hero">
        <div className="ln-doc__wrap">
          <nav className="ln-post__crumbs" aria-label="breadcrumb">
            <Link to="/">{t("Home")}</Link>
            <span aria-hidden="true">/</span>
            <Link to="/#news">{t("News")}</Link>
          </nav>
          {status === "loading" ? (
            <>
              <span className="ln-skel" style={{ width: "70%", height: 34 }} />
              <span className="ln-skel" style={{ width: 220 }} />
            </>
          ) : status === "notfound" ? (
            <h1 className="ln-doc__title">{t("ln_blog_notfound")}</h1>
          ) : (
            <>
              <h1 className="ln-doc__title ln-post__title">{article.title}</h1>
              <ul className="ln-post__meta">
                {article.date ? (
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                    {formatDate(article.date)}
                  </li>
                ) : null}
                {article.author ? (
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    {t("ln_blog_author")}: {article.author}
                  </li>
                ) : null}
                <li>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                  {t("ln_blog_read", { min: readMinutes })}
                </li>
              </ul>
            </>
          )}
        </div>
      </div>

      <div className="ln-doc__wrap ln-doc__body">
        <article className="ln-doc__card ln-post__card">
          {status === "loading" ? (
            <div className="ln-doc__skeleton">
              <span className="ln-skel ln-post__cover-skel" />
              {[90, 70, 85, 60, 80].map((w, i) => (
                <span key={i} className="ln-skel" style={{ width: `${w}%` }} />
              ))}
            </div>
          ) : status === "notfound" ? (
            <div className="ln-post__empty">
              <p className="ln-lead">{t("ln_blog_notfound_text")}</p>
              <Link to="/#news" className="ln-btn ln-btn_primary">
                {t("ln_blog_all")}
              </Link>
            </div>
          ) : (
            <>
              {article.image && !/\/$/.test(article.image) ? (
                <figure className="ln-post__cover ln-fit">
                  {/* rasm to'liq ko'rinadi (kesilmaydi), bo'sh joyni shu rasmning xira nusxasi to'ldiradi */}
                  <img className="ln-fit__bg" src={article.image} alt="" aria-hidden="true" />
                  <img className="ln-fit__img" src={article.image} alt={article.title} onError={(e) => (e.currentTarget.parentElement.style.display = "none")} />
                </figure>
              ) : null}
              {html ? (
                <div className="ln-prose" dangerouslySetInnerHTML={{ __html: html }} />
              ) : (
                <p className="ln-prose" style={{ whiteSpace: "pre-wrap" }}>{article.text}</p>
              )}
            </>
          )}
        </article>

        <aside className="ln-doc__aside">
          {more.length ? (
            <div className="ln-doc__box">
              <strong>{t("ln_blog_more")}</strong>
              <ul className="ln-post__list">
                {more.map((a) => (
                  <li key={a.id}>
                    <button type="button" onClick={() => navigate(`/blogopen?id=${a.id}`)}>
                      <span className="ln-post__thumb">
                        {a.image && !/\/$/.test(a.image) ? <img src={a.image} alt="" loading="lazy" /> : null}
                      </span>
                      <span className="ln-post__item">
                        <em>{formatDate(a.date)}</em>
                        {a.title}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="ln-doc__box">
            <strong>{t("ln_blog_share")}</strong>
            <p>{t("ln_blog_share_text")}</p>
            <button type="button" className="ln-btn ln-btn_outline" onClick={copyLink} disabled={status !== "ok"}>
              {copied ? t("ln_blog_copied") : t("ln_blog_copy")}
            </button>
          </div>
          <Link to="/#news" className="ln-doc__print">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            {t("ln_blog_all")}
          </Link>
        </aside>
      </div>
    </div>
  );
}
export default withTranslation()(BlogOpen);
